import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'CustomPageStyle/byToken';

interface CustomPageStyleResponse {
	html?: string;
	hasCustomization: boolean;
	error?: string;
}

export const useCustomPageStyleByType = ({
	type,
	enabled = true,
	impersonationContext,
}: {
	type: Api.CustomPageType;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();

	return useQuery<CustomPageStyleResponse>({
		enabled,
		queryFn: async (): Promise<CustomPageStyleResponse> => {
			try {
				const response = await userSession.webServiceHelper.callAsync<Api.CustomPageStyle>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `CustomPageStyle/${type}`,
					}),
					'GET'
				);

				if (!response || !response.css || response.css.trim() === '') {
					return { hasCustomization: false, error: 'No custom styles found' };
				}

				return {
					hasCustomization: true,
					html: response.css,
				};
			} catch (networkError) {
				const apiError = Api.asApiError(networkError);
				if (apiError.systemCode === 404) {
					return { hasCustomization: false, error: 'No custom styles found' };
				}
				console.error('Network error fetching custom page style:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		queryKey: [BASE_QUERY_KEY, type, impersonationContext],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};

export const invalidateCustomPageStyleByToken = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
